import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';
import './FeaturedPosts.css';

const PLATFORM_LABELS = {
  youtube: { label: 'YouTube', icon: '▶', color: '#ff0000' },
  instagram: { label: 'Instagram', icon: '📷', color: '#e4405f' },
  blog: { label: 'Blog', icon: '📝', color: '#1a8917' }
};

function FeaturedPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    axios.get('/api/submissions/featured')
      .then(res => {
        if (Array.isArray(res.data)) setPosts(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (posts.length === 0 || !trackRef.current) return;

    const track = trackRef.current;
    let animationId;
    let scrollPos = 0;
    const speed = 0.5;

    const animate = () => {
      scrollPos += speed;
      const maxScroll = track.scrollWidth / 2;
      if (scrollPos >= maxScroll) {
        scrollPos = 0;
      }
      track.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationId);
  }, [posts]);

  if (loading || !Array.isArray(posts) || posts.length === 0) return null;

  const getTitle = (post) => post.channel_title || post.account_name || post.blog_title;
  const getStat = (post) => {
    if (post.subscribers) return `${post.subscribers} subscribers`;
    if (post.followers) return `${post.followers} followers`;
    if (post.views_per_month) return `${post.views_per_month} views/mo`;
    return null;
  };
  const getUrl = (post) => post.channel_url || post.instagram_url || post.blog_url;
  const getDescription = (post) => post.channel_description || post.account_description || post.blog_description;
  const getPhotos = (post) => {
    try { return JSON.parse(post.photos); } catch { return []; }
  };

  const renderCard = (post, idx) => {
    const platform = PLATFORM_LABELS[post.platform] || {};
    const photos = getPhotos(post);
    return (
      <div key={`${post.platform}-${post.id}-${idx}`} className="featured-card">
        <div className="featured-card-header">
          <span className="featured-platform" style={{ backgroundColor: platform.color }}>
            {platform.icon} {platform.label}
          </span>
          {post.is_featured === 1 && <span className="featured-badge">Featured</span>}
        </div>
        {photos.length > 0 && (
                <img src={getImageUrl(photos[0])} alt="" className="featured-card-photo" />
        )}
        <div className="featured-card-body">
          <h3 className="featured-card-title">{getTitle(post)}</h3>
          {getStat(post) && <p className="featured-card-stat">{getStat(post)}</p>}
          <div className="featured-card-tags">
            {post.niche && <span className="featured-tag">{post.niche}</span>}
            {post.location && <span className="featured-tag">{post.location}</span>}
          </div>
          {getDescription(post) && (
            <p className="featured-card-desc">{getDescription(post).substring(0, 100)}{getDescription(post).length > 100 ? '...' : ''}</p>
          )}
          {getUrl(post) && (
            <a href={getUrl(post)} target="_blank" rel="noopener noreferrer" className="featured-card-link">
              Visit {platform.label}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="featured-posts">
      <h2 className="featured-title">Featured Creators</h2>
      <p className="featured-subtitle">Check out these top creators to reach out for sponsorship opportunities</p>
      <div className="featured-scroll-wrapper">
        <div className="featured-track" ref={trackRef}>
          {posts.map((post, i) => renderCard(post, i))}
          {posts.map((post, i) => renderCard(post, `dup-${i}`))}
        </div>
      </div>
    </div>
  );
}

export default FeaturedPosts;
